'use client';

import { Close, PersonOutline } from '@mui/icons-material';
import MenuIcon from '@mui/icons-material/Menu';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import { InputBaseProps } from '@mui/material';
import React, { MouseEventHandler, useEffect, useRef } from 'react';
import { ROUTES } from '../../../constants';
import { useTranslations } from 'next-intl';
import { useNavigateTo, useUser } from '../../hooks';
import { noop } from '../../utils';
import { IconButton } from '../buttons';
import { ProfileMenu } from '../header/mainapp/profilemenu/ProfileMenu';
import { SearchWrapper, StyledInputBase } from './styles';

type SearchBarProps = Pick<InputBaseProps, 'onChange' | 'onKeyUp' | 'placeholder'> & {
    isCompact: boolean;
    isFocused?: boolean;
    onMenuClick: MouseEventHandler<HTMLButtonElement>;
    onCancel?: MouseEventHandler<HTMLButtonElement>;
    onSearch?: (term: string) => void;
    onFocusChange?: (focus: boolean) => void;
    value: string;
};

function SearchBar({
    isCompact,
    isFocused = false,
    onMenuClick,
    onChange,
    onFocusChange,
    onCancel,
    onSearch,
    onKeyUp,
    value,
    placeholder
}: SearchBarProps) {
    const t = useTranslations('searchbar');
    const { user } = useUser({ withProfilePicture: true });
    const inputRef = useRef<HTMLElement>(undefined);
    const navigateTo = useNavigateTo();
    const isLoggedIn = user !== undefined;

    const handleClickButton = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        if (isCompact) {
            // On compact, open the burger menu
            onMenuClick(e);
        } else if (!value) {
            // With no search term, just toggle the suggestion dropdown as if clicking on the input field
            onFocusChange?.(!isFocused);
        } else if (onSearch) {
            // Trigger the search when there is a search term
            onSearch(value);
            onFocusChange?.(false);
        } else noop();
    };

    // If isFocused is false or is being set to false, the SeachBar input is blurred, to prevent side effects with displaying SearchSuggestions
    useEffect(() => {
        if (!isFocused && inputRef.current) {
            inputRef.current.blur();
        }
    }, [isFocused]);

    return (
        <SearchWrapper isElevated={isFocused} isFocused={isFocused}>
            <IconButton
                aria-label='search-button'
                icon={isCompact ? <MenuIcon /> : <SearchOutlinedIcon />}
                onClick={handleClickButton}
            />
            <StyledInputBase
                placeholder={placeholder ?? t('placeholder')}
                inputProps={{ 'aria-label': 'search' }}
                onChange={onChange}
                onKeyUp={onKeyUp}
                value={value}
                onFocus={() => onFocusChange?.(true)}
                onBlur={() => {
                    onFocusChange && onFocusChange(false);
                }}
                inputRef={inputRef}
            />
            {isCompact &&
                (isLoggedIn ? (
                    <ProfileMenu user={user} />
                ) : (
                    <IconButton icon={<PersonOutline />} onClick={() => navigateTo(ROUTES.Login)} />
                ))}
            {value.length > 0 && <IconButton icon={<Close />} onClick={onCancel} />}
        </SearchWrapper>
    );
}

export { SearchBar };
